/**
 * @fileoverview Genogram State Management Store
 * 
 * This file implements the central state management for the genogram application
 * using Zustand. It manages the complete application state including nodes (people),
 * edges (relationships), selection, and canvas view state.
 * 
 * Key Features:
 * - Centralized state management for all genogram data
 * - Immutable state updates with automatic re-renders
 * - Canvas view state (zoom, pan) management
 * - Selection tracking and multi-selection support
 * - Scene loading/saving and reset functionality
 * - Automatic metadata tracking (created/updated timestamps)
 * 
 * Architecture:
 * - Uses Zustand for lightweight, performant state management
 * - Follows immutable update patterns
 * - Provides computed derived state
 * - Integrates with React components via hooks
 * 
 * @author Claude Code
 * @version 1.0.0
 */

import { create } from 'zustand';
import { Scene, Node, Edge } from '../core/models/scene';
import { ShapeLimitConfig, ShapeLimitResult } from '../utils/shapeLimitUtils';
import { IFrame } from '../core/frames/IFrame';
import { Logger } from '../utils/logger';

const logger = Logger.getLoggerWithPrefix('useDiagramStore');

/**
 * Represents the current view state of the canvas stage.
 * Controls zoom level and pan position for the viewport.
 */
type StageState = { 
  /** Current zoom/scale level (0.2 to 2.0) */
  scale: number; 
  
  /** Horizontal pan offset in pixels */
  offsetX: number; 
  
  /** Vertical pan offset in pixels */
  offsetY: number; 
};

/**
 * Main store interface defining all state and actions for the genogram application.
 * 
 * This store manages the complete application state including:
 * - Scene data (nodes and edges)
 * - Selection state for multi-selection
 * - Canvas view state (zoom/pan)
 * - CRUD operations for all entities
 * - Scene import/export functionality
 */
interface DiagramStore {
  // Core data state
  
  /** Complete scene data including metadata */
  scene: Scene;
  
  /** Array of person nodes in the genogram */
  nodes: Node[];
  
  /** Array of relationship edges connecting nodes */
  edges: Edge[];

  /** Array of frames for organizing content */
  frames: IFrame[];

  /** Set of currently selected node/edge IDs */
  selectedIds: Set<string>;
  
  /** Current canvas view state (zoom/pan) */
  stage: StageState;
  
  /** Set of node ids currently being dragged (for commit policy) */
  draggingIds: Set<string>;
  /** Dirty ids changed since last projection sync */
  dirtyIds: Set<string>;
  
  /** User's write permission (set by context) */
  canWrite: boolean;
  
  /** Shape limit checker function (set by components with entitlements access) */
  shapeLimitChecker?: (config: ShapeLimitConfig) => ShapeLimitResult;
  
  /** Flag to indicate if we're in initial loading phase (skip shape limit checks) */
  isInitialLoading: boolean;

  /** Tracks templates with example data that have been loaded */
  templatesWithExampleData: Set<string>;

  /** Flag to indicate if example data has been cleared by the user */
  exampleDataCleared: boolean;

  // Actions for managing state
  
  /** Update canvas view state (zoom/pan) */
  setStage: (s: Partial<StageState>) => void;
  
  /** Set the current selection */
  setSelected: (ids: Set<string>) => void;

  /** Track drag state for a node */
  setDragging: (id: string, dragging: boolean) => void;

  /** Mark that a template with example data has been loaded */
  markTemplateWithExampleData: (templateId: string) => void;

  /** Mark that example data has been cleared */
  markExampleDataCleared: () => void;

  /** Reset example data tracking (for new diagrams) */
  resetExampleDataTracking: () => void;
  
  /** Add a new person node to the genogram */
  addNode: (node: Node, fromYjs?: boolean) => void;

  /** Batch add multiple nodes in a single state mutation */
  batchAddNodes: (nodes: Node[], fromYjs?: boolean) => void;

  /** Update properties of an existing node */
  updateNode: (id: string, updates: Partial<Node>, fromYjs?: boolean) => void;

  /** Batch update multiple nodes at once */
  updateNodes: (updates: Array<{ id: string; updates: Partial<Node> }>, fromYjs?: boolean) => void;

  /** Move a node to a new position */
  moveNode: (id: string, pos: { x: number; y: number }, fromYjs?: boolean) => void;

  /** Remove a node and all connected edges */
  deleteNode: (id: string, fromYjs?: boolean) => void;

  /** Add a new relationship edge */
  addEdge: (edge: Edge, fromYjs?: boolean) => void;

  /** Batch add multiple edges in a single state mutation */
  batchAddEdges: (edges: Edge[], fromYjs?: boolean) => void;

  /** Remove a relationship edge */
  deleteEdge: (id: string, fromYjs?: boolean) => void;

  /** Update properties of an existing relationship edge */
  updateEdge: (id: string, updates: Partial<Edge>, fromYjs?: boolean) => void;

  /** Batch update multiple edges at once */
  updateEdges: (updates: Array<{ id: string; updates: Partial<Edge> }>, fromYjs?: boolean) => void;

  /** Set frames array */
  setFrames: (frames: IFrame[]) => void;

  /** Zoom in/out around a center point */
  zoom: (delta: number, cx: number, cy: number) => void;
  
  /** Set the shape limit checker function */
  setShapeLimitChecker: (checker: ((config: ShapeLimitConfig) => ShapeLimitResult) | undefined) => void;
  
  /** Set the initial loading flag */
  setInitialLoading: (loading: boolean) => void;
  
  /** Set the write permission */
  setCanWrite: (canWrite: boolean) => void;
  
  /** Load a complete scene, replacing current data */
  loadScene: (scene: Scene, options?: { preserveSelection?: boolean }) => void;
  
  /** Export the current scene data */
  exportScene: () => Scene;
  
  /** Reset to initial demo scene */
  reset: () => void;
  
  /** Atomically fetch and clear dirty ids */
  consumeDirtyIds: () => string[];
}

/**
 * Initial demo scene with a multi-generational family tree.
 * 
 * This scene demonstrates the genogram features including:
 * - Multiple generations (grandparents, parents, children)
 * - Different cultural backgrounds (Italy, UK, Vietnam)
 * - Marriage relationships including divorce
 * - Parent-child relationships
 * - Deceased family members
 * - Mixed heritage representation
 * 
 * The scene serves as both a demo and a starting point for new users.
 */
// Commented out - not used anymore since we're loading from Yjs
/* const initialScene: Scene = {
  version: 1,
  nodes: [
    // Grandparents - Italian side
    {
      id: 'gf-italy',
      type: 'person',
      name: 'Grandfather',
      sex: 'male',
      x: 200,
      y: 100,
      location: 'Italy',
      deceased: true
    },
    {
      id: 'gm-italy',
      type: 'person',
      name: 'Grandmother',
      sex: 'female',
      x: 350,
      y: 100,
      location: 'Italy'
    },
    // Grandparents - UK/Vietnam side
    {
      id: 'gf-uk',
      type: 'person',
      name: 'Grandfather',
      sex: 'male',
      x: 600,
      y: 100,
      location: 'UK'
    },
    {
      id: 'gm-vietnam',
      type: 'person',
      name: 'Grandmother',
      sex: 'female',
      x: 750,
      y: 100,
      location: 'Vietnam'
    },
    // Parents generation
    {
      id: 'father',
      type: 'person',
      name: 'Father',
      sex: 'male',
      x: 275,
      y: 300,
      location: 'Italy',
      deceased: true
    },
    {
      id: 'uncle',
      type: 'person',
      name: 'Uncle',
      sex: 'male',
      x: 150,
      y: 300,
      location: 'Italy'
    },
    {
      id: 'aunt',
      type: 'person',
      name: 'Aunt',
      sex: 'female',
      x: 400,
      y: 300,
      location: 'Italy'
    },
    {
      id: 'mother',
      type: 'person',
      name: 'Mother',
      sex: 'female',
      x: 675,
      y: 300,
      attrs: {
        mixedHeritage: true,
        heritageColors: ['#4682B4', '#90EE90']
      }
    },
    // Current generation (siblings)
    {
      id: 'child1',
      type: 'person',
      name: 'Child 1',
      sex: 'male',
      x: 375,
      y: 500
    },
    {
      id: 'child2',
      type: 'person',
      name: 'Child 2',
      sex: 'female',
      x: 475,
      y: 500
    },
    {
      id: 'child3',
      type: 'person',
      name: 'Child 3',
      sex: 'female',
      x: 575,
      y: 500
    }
  ],
  edges: [
    // Italian grandparents marriage
    {
      id: 'marriage-1',
      type: 'marriage',
      from: 'gf-italy',
      to: 'gm-italy'
    },
    // UK/Vietnam grandparents marriage
    {
      id: 'marriage-2',
      type: 'marriage',
      from: 'gf-uk',
      to: 'gm-vietnam'
    },
    // Children from Italian grandparents
    {
      id: 'child-1',
      type: 'child',
      from: 'gf-italy',
      to: 'uncle'
    },
    {
      id: 'child-2',
      type: 'child',
      from: 'gf-italy',
      to: 'father'
    },
    {
      id: 'child-3',
      type: 'child',
      from: 'gf-italy',
      to: 'aunt'
    },
    // Child from UK/Vietnam grandparents
    {
      id: 'child-4',
      type: 'child',
      from: 'gf-uk',
      to: 'mother'
    },
    // Parents marriage (divorced)
    {
      id: 'marriage-3',
      type: 'marriage',
      from: 'father',
      to: 'mother',
      attrs: {
        divorced: true
      }
    },
    // Children from parents
    {
      id: 'child-5',
      type: 'child',
      from: 'father',
      to: 'child1'
    },
    {
      id: 'child-6',
      type: 'child',
      from: 'father',
      to: 'child2'
    },
    {
      id: 'child-7',
      type: 'child',
      from: 'father',
      to: 'child3'
    }
  ],
  meta: {
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
}; */

/**
 * Main Zustand store for diagram application state.
 * 
 * This store provides reactive state management for diagram editing.
 * Components can subscribe to specific parts of the state and will automatically
 * re-render when that data changes.
 * 
 * Usage:
 * ```tsx
 * const { nodes, addNode, setSelected } = useDiagramStore();
 * 
 * // Add a new shape
 * addNode({ id: 'new-shape', name: 'Shape 1', ... });
 * 
 * // Select multiple nodes
 * setSelected(new Set(['node1', 'node2']));
 * ```
 * 
 * State Management Features:
 * - Immutable updates ensure React re-renders correctly
 * - Automatic metadata tracking (created/updated timestamps)
 * - Derived state computation for efficient rendering
 * - Batch updates for performance optimization
 * 
 * @returns DiagramStore object with state and actions
 */
// Start with empty scene instead of demo data
const emptyScene: Scene = {
  version: 1,
  nodes: [],
  edges: [],
  meta: {
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
};

export const useDiagramStore = create<DiagramStore>((set, get) => ({
  scene: emptyScene,
  nodes: emptyScene.nodes,
  edges: emptyScene.edges,
  frames: [],
  selectedIds: new Set(),
  stage: { scale: 1, offsetX: 0, offsetY: 0 },
  draggingIds: new Set<string>(),
  dirtyIds: new Set<string>(),
  canWrite: false, // Fail closed until the access context resolves.
  isInitialLoading: true,
  templatesWithExampleData: new Set<string>(),
  exampleDataCleared: false,
  
  setStage: (s) => set((state) => ({ 
    stage: { ...state.stage, ...s } 
  })),
  
  setSelected: (ids) => set({ 
    selectedIds: new Set(ids) 
  }),
  
  setDragging: (id, dragging) => set((state) => {
    const next = new Set(state.draggingIds);
    if (dragging) next.add(id); else next.delete(id);
    return { draggingIds: next } as any;
  }),
  
  setShapeLimitChecker: (checker) => set({ shapeLimitChecker: checker }),

  setInitialLoading: (loading) => set({ isInitialLoading: loading }),

  setCanWrite: (canWrite) => set({ canWrite }),

  markTemplateWithExampleData: (templateId) => set((state) => {
    const next = new Set(state.templatesWithExampleData);
    next.add(templateId);
    return { templatesWithExampleData: next, exampleDataCleared: false };
  }),

  markExampleDataCleared: () => set({ exampleDataCleared: true }),

  resetExampleDataTracking: () => set({
    templatesWithExampleData: new Set(),
    exampleDataCleared: false
  }),
  
  addNode: (node, fromYjs = false) => set((state) => {
    // Check write permissions (but allow during initial loading or when syncing from Yjs)
    if (!state.canWrite && !state.isInitialLoading && !fromYjs) {
      return state;
    }

    const exists = state.nodes.some((n) => n.id === node.id);
    // Assign default zIndex for brand-new nodes when not provided.
    // Tables should default to the lowest zIndex so other shapes can be dragged onto them easily.
    const baseMaxZ = state.nodes.reduce((m, n) => Math.max(m, Number((n as any).zIndex ?? 0)), 0);
    const baseMinZ = state.nodes.reduce((m, n) => Math.min(m, Number((n as any).zIndex ?? 0)), 0);
    const nextDefaultZ =
      (node as any)?.type === 'table'
        ? (Number.isFinite(baseMinZ) ? baseMinZ : 0) - 1
        : (Number.isFinite(baseMaxZ) ? baseMaxZ : 0) + 1;
    const nodeWithZ = (!exists && (node as any).zIndex == null)
      ? { ...node, zIndex: nextDefaultZ }
      : node;
    const nextNodes = exists
      ? state.nodes.map((n) => (n.id === node.id ? { ...n, ...nodeWithZ } : n))
      : [...state.nodes, nodeWithZ];

    const sceneExists = state.scene.nodes.some((n) => n.id === node.id);
    const nextSceneNodes = sceneExists
      ? state.scene.nodes.map((n) => (n.id === node.id ? { ...n, ...nodeWithZ } : n))
      : [...state.scene.nodes, nodeWithZ];

    const nextDirty = new Set(state.dirtyIds);
    if (!fromYjs) {
      nextDirty.add(node.id);
    }
    try { logger.debug('Debug SDI: diagramStore.addNode', { id: node.id, exists, fromYjs }); } catch {}
    return {
      nodes: nextNodes,
      edges: state.edges,
      dirtyIds: nextDirty,
      scene: {
        ...state.scene,
        nodes: nextSceneNodes,
        meta: {
          createdAt: state.scene.meta?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      }
    } as any;
  }),

  batchAddNodes: (newNodes, fromYjs = false) => set((state) => {
    if (!state.canWrite && !state.isInitialLoading && !fromYjs) {
      return state;
    }
    if (newNodes.length === 0) return state;

    const existingIds = new Set(state.nodes.map(n => n.id));
    const sceneExistingIds = new Set(state.scene.nodes.map(n => n.id));
    const baseMaxZ = state.nodes.reduce((m, n) => Math.max(m, Number((n as any).zIndex ?? 0)), 0);
    const baseMinZ = state.nodes.reduce((m, n) => Math.min(m, Number((n as any).zIndex ?? 0)), 0);

    const toAdd: Node[] = [];
    const toUpdate = new Map<string, Node>();

    for (const node of newNodes) {
      if (existingIds.has(node.id)) {
        toUpdate.set(node.id, node);
      } else {
        let nodeWithZ = node;
        if ((node as any).zIndex == null) {
          const nextZ = (node as any)?.type === 'table'
            ? (Number.isFinite(baseMinZ) ? baseMinZ : 0) - 1 - toAdd.length
            : (Number.isFinite(baseMaxZ) ? baseMaxZ : 0) + 1 + toAdd.length;
          nodeWithZ = { ...node, zIndex: nextZ } as any;
        }
        toAdd.push(nodeWithZ);
        existingIds.add(node.id);
      }
    }

    let nextNodes = toUpdate.size > 0
      ? state.nodes.map(n => toUpdate.has(n.id) ? { ...n, ...toUpdate.get(n.id)! } : n)
      : state.nodes;
    if (toAdd.length > 0) {
      nextNodes = [...nextNodes, ...toAdd];
    }

    let nextSceneNodes = toUpdate.size > 0
      ? state.scene.nodes.map(n => toUpdate.has(n.id) ? { ...n, ...toUpdate.get(n.id)! } : n)
      : state.scene.nodes;
    const sceneToAdd = toAdd.filter(n => !sceneExistingIds.has(n.id));
    if (sceneToAdd.length > 0) {
      nextSceneNodes = [...nextSceneNodes, ...sceneToAdd];
    }

    const nextDirty = new Set(state.dirtyIds);
    if (!fromYjs) {
      for (const node of newNodes) nextDirty.add(node.id);
    }

    return {
      nodes: nextNodes,
      edges: state.edges,
      dirtyIds: nextDirty,
      scene: {
        ...state.scene,
        nodes: nextSceneNodes,
        meta: {
          createdAt: state.scene.meta?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      }
    } as any;
  }),

  updateNode: (id, updates, fromYjs = false) => set((state) => {
    // Check write permissions (but allow during initial loading or when syncing from Yjs)
    if (!state.canWrite && !state.isInitialLoading && !fromYjs) {
      return state;
    }
    
    return {
      nodes: state.nodes.map((n) => 
        n.id === id ? { ...n, ...updates } : n
      ),
      edges: state.edges,
      dirtyIds: fromYjs ? new Set<string>(state.dirtyIds) : new Set<string>([...state.dirtyIds, id]),
      scene: {
        ...state.scene,
        nodes: state.scene.nodes.map((n) => 
          n.id === id ? { ...n, ...updates } : n
        ),
        meta: {
          createdAt: state.scene.meta?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      }
    };
  }),
  
  updateNodes: (nodeUpdates, fromYjs = false) => set((state) => {
    // Check write permissions (but allow during initial loading or when syncing from Yjs)
    if (!state.canWrite && !state.isInitialLoading && !fromYjs) {
      return state;
    }
    
    // Create a map for quick lookup
    const updateMap = new Map(nodeUpdates.map(u => [u.id, u.updates]));
    const nextDirty = new Set(state.dirtyIds);
    if (!fromYjs) {
      nodeUpdates.forEach((u) => nextDirty.add(u.id));
    }
    
    return {
      nodes: state.nodes.map((n) => {
        const updates = updateMap.get(n.id);
        return updates ? { ...n, ...updates } : n;
      }),
      edges: state.edges,
      dirtyIds: nextDirty,
      scene: {
        ...state.scene,
        nodes: state.scene.nodes.map((n) => {
          const updates = updateMap.get(n.id);
          return updates ? { ...n, ...updates } : n;
        }),
        meta: {
          createdAt: state.scene.meta?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      }
    };
  }),
  
  moveNode: (id, pos, fromYjs = false) => set((state) => {
    // Check write permissions (but allow during initial loading or when syncing from Yjs)
    if (!state.canWrite && !state.isInitialLoading && !fromYjs) {
      return state;
    }
    
    return {
      nodes: state.nodes.map((n) => 
        n.id === id ? { ...n, ...pos } : n
      ),
    edges: state.edges,
    dirtyIds: fromYjs ? new Set<string>(state.dirtyIds) : new Set<string>([...state.dirtyIds, id]),
    scene: {
      ...state.scene,
      nodes: state.scene.nodes.map((n) => 
        n.id === id ? { ...n, ...pos } : n
      ),
      meta: {
        createdAt: state.scene.meta?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    }
  };
  }),
  
  deleteNode: (id, fromYjs = false) => set((state) => {
    // Check write permissions (but allow during initial loading or when syncing from Yjs)
    if (!state.canWrite && !state.isInitialLoading && !fromYjs) {
      return state;
    }
    
    try { logger.debug('Debug SDI: diagramStore.deleteNode', { id, fromYjs }); } catch {}
    const newNodes = state.nodes.filter((n) => n.id !== id);
    const newEdges = state.edges.filter((e) => 
      e.from !== id && e.to !== id
    );
    
    return {
      nodes: newNodes,
      edges: newEdges,
      dirtyIds: fromYjs ? new Set<string>(state.dirtyIds) : new Set<string>([...state.dirtyIds, id]),
      scene: {
        ...state.scene,
        nodes: newNodes,
        edges: newEdges,
        meta: {
          createdAt: state.scene.meta?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      }
    };
  }),
  
  addEdge: (edge, fromYjs = false) => set((state) => {
    // Check write permissions (but allow during initial loading or when syncing from Yjs)
    if (!state.canWrite && !state.isInitialLoading && !fromYjs) {
      return state;
    }

    const exists = state.edges.some((e) => e.id === edge.id);
    const nextEdges = exists
      ? state.edges.map((e) => (e.id === edge.id ? { ...e, ...edge } : e))
      : [...state.edges, edge];

    const sceneExists = state.scene.edges.some((e) => e.id === edge.id);
    const nextSceneEdges = sceneExists
      ? state.scene.edges.map((e) => (e.id === edge.id ? { ...e, ...edge } : e))
      : [...state.scene.edges, edge];

    return {
      nodes: state.nodes,
      edges: nextEdges,
      scene: {
        ...state.scene,
        edges: nextSceneEdges,
        meta: {
          createdAt: state.scene.meta?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      }
    } as any;
  }),

  batchAddEdges: (newEdges, fromYjs = false) => set((state) => {
    if (!state.canWrite && !state.isInitialLoading && !fromYjs) {
      return state;
    }
    if (newEdges.length === 0) return state;

    const existingIds = new Set(state.edges.map(e => e.id));
    const sceneExistingIds = new Set(state.scene.edges.map(e => e.id));

    const toAdd: Edge[] = [];
    const toUpdate = new Map<string, Edge>();

    for (const edge of newEdges) {
      if (existingIds.has(edge.id)) {
        toUpdate.set(edge.id, edge);
      } else {
        toAdd.push(edge);
        existingIds.add(edge.id);
      }
    }

    let nextEdges = toUpdate.size > 0
      ? state.edges.map(e => toUpdate.has(e.id) ? { ...e, ...toUpdate.get(e.id)! } : e)
      : state.edges;
    if (toAdd.length > 0) {
      nextEdges = [...nextEdges, ...toAdd];
    }

    let nextSceneEdges = toUpdate.size > 0
      ? state.scene.edges.map(e => toUpdate.has(e.id) ? { ...e, ...toUpdate.get(e.id)! } : e)
      : state.scene.edges;
    const sceneToAdd = toAdd.filter(e => !sceneExistingIds.has(e.id));
    if (sceneToAdd.length > 0) {
      nextSceneEdges = [...nextSceneEdges, ...sceneToAdd];
    }

    return {
      nodes: state.nodes,
      edges: nextEdges,
      scene: {
        ...state.scene,
        edges: nextSceneEdges,
        meta: {
          createdAt: state.scene.meta?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      }
    } as any;
  }),

  deleteEdge: (id, fromYjs = false) => set((state) => {
    // Check write permissions (but allow during initial loading or when syncing from Yjs)
    if (!state.canWrite && !state.isInitialLoading && !fromYjs) {
      return state;
    }
    
    return {
      nodes: state.nodes,
      edges: state.edges.filter((e) => e.id !== id),
      scene: {
        ...state.scene,
        edges: state.scene.edges.filter((e) => e.id !== id),
        meta: {
          createdAt: state.scene.meta?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      }
    };
  }),
  
  updateEdge: (id, updates, fromYjs = false) => set((state) => {
    // Check write permissions (but allow during initial loading or when syncing from Yjs)
    if (!state.canWrite && !state.isInitialLoading && !fromYjs) {
      return state;
    }
    
    const nextEdges = state.edges.map((e) => (e.id === id ? { ...e, ...updates, attrs: { ...(e.attrs || {}), ...(updates as any).attrs } } : e));
    const nextSceneEdges = state.scene.edges.map((e) => (e.id === id ? { ...e, ...updates, attrs: { ...(e.attrs || {}), ...(updates as any).attrs } } : e));
    return {
      nodes: state.nodes,
      edges: nextEdges,
      scene: {
        ...state.scene,
        edges: nextSceneEdges,
        meta: {
          createdAt: state.scene.meta?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      }
    } as any;
  }),

  updateEdges: (edgeUpdates, fromYjs = false) => set((state) => {
    if (!state.canWrite && !state.isInitialLoading && !fromYjs) {
      return state;
    }

    const updateMap = new Map(edgeUpdates.map(u => [u.id, u.updates]));

    return {
      nodes: state.nodes,
      edges: state.edges.map((e) => {
        const updates = updateMap.get(e.id);
        return updates ? { ...e, ...updates, attrs: { ...(e.attrs || {}), ...(updates as any).attrs } } : e;
      }),
      scene: {
        ...state.scene,
        edges: state.scene.edges.map((e) => {
          const updates = updateMap.get(e.id);
          return updates ? { ...e, ...updates, attrs: { ...(e.attrs || {}), ...(updates as any).attrs } } : e;
        }),
        meta: {
          createdAt: state.scene.meta?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      }
    } as any;
  }),

  setFrames: (frames) => set({ frames }),

  zoom: (delta, _cx, _cy) => {
    const { stage } = get();
    const scale = Math.min(2, Math.max(0.01, stage.scale + delta));
    set({ stage: { ...stage, scale } });
  },
  
  loadScene: (scene, options) => set((state) => {
    try { logger.debug('Debug SDI: diagramStore.loadScene', { nodes: scene.nodes?.length || 0, edges: scene.edges?.length || 0 }); } catch {}
    const selectedIds = options?.preserveSelection && state.selectedIds.size > 0
      ? (() => {
          const sceneIds = new Set([
            ...(scene.nodes ?? []).map((node) => node.id),
            ...(scene.edges ?? []).map((edge) => edge.id),
          ]);
          return new Set([...state.selectedIds].filter((id) => sceneIds.has(id)));
        })()
      : new Set<string>();
    return {
      scene,
      nodes: scene.nodes,
      edges: scene.edges,
      selectedIds
      // Don't set isInitialLoading to false here - let it be set by the Yjs loading process
    } as any;
  }),
  
  exportScene: () => get().scene,
  
  reset: () => set({
    scene: emptyScene,
    nodes: emptyScene.nodes,
    edges: emptyScene.edges,
    frames: [],
    selectedIds: new Set(),
    stage: { scale: 1, offsetX: 0, offsetY: 0 }
  }),
  
  consumeDirtyIds: () => {
    const ids = Array.from(get().dirtyIds);
    set({ dirtyIds: new Set<string>() });
    return ids;
  }
}));

// Expose store on window for E2E tests (works in production builds)
// This allows tests to access the store without relying on source maps or dev paths
if (typeof window !== 'undefined') {
  (window as any).__E2E_DIAGRAM_STORE__ = useDiagramStore;
  (window as any).useDiagramStore = useDiagramStore;
}
